export type InputMethod = 'spraak' | 'toetsenbord' | 'gemengd'

export type CaptureStatus =
  | 'concept'
  | 'verwerken'
  | 'klaar'
  | 'mislukt'
  | 'bevestigd'
  | 'ongedaan'
  | 'verlopen'

/** The relation a capture was started from (contact or organisation page). */
export interface CaptureContext {
  type: 'contact' | 'organisatie'
  id: string
}

export interface VoiceStatus {
  available: boolean
  reason?: string | null
  notice_version: number
  notice_seen_version: number
  max_chars: number
}

/** The detail of a capture (`VoiceCaptureOut`). */
export interface VoiceCapture {
  id: string
  client_capture_id: string
  status: CaptureStatus
  transcript: string | null
  context?: CaptureContext | null
  context_person_id?: string | null
  context_organisation_id?: string | null
  input_method: InputMethod | null
  result: VoiceResult | null
  decisions: unknown
  created_refs?: Partial<CreatedRefs> | null
  undo_until?: string | null
  error_code?: string | null
  error_message?: string | null
  updated_at?: string
}

export interface CreateCaptureBody {
  client_capture_id: string
  transcript: string
  context?: CaptureContext | null
  input_method: InputMethod
  notice_version: number
}

export interface PatchCaptureBody {
  transcript: string
  input_method: InputMethod
  /** Present (also as null) changes the context; absent leaves it alone. */
  context?: CaptureContext | null
}

/** What is kept in `localStorage` when saving to the server fails. */
export interface LocalDraft {
  text: string
  savedAt: number
  context: CaptureContext | null
  inputMethod?: InputMethod
}

/** The relation a capture was started from, from either shape the API/tests use. */
export function captureContext(capture: VoiceCapture): CaptureContext | null {
  if (capture.context) return capture.context
  if (capture.context_person_id) return { type: 'contact', id: capture.context_person_id }
  if (capture.context_organisation_id) return { type: 'organisatie', id: capture.context_organisation_id }
  return null
}

// --- The result document (`VoiceResultV1`, schemas.py) ------------------------------------

export type Certainty = 'hoog' | 'middel' | 'laag'
export type MentionCertainty = 'zeker' | 'controleer' | 'kiezen' | 'niet_gevonden'
export type ActivityKind = 'call' | 'meeting' | 'email' | 'note'
export type ScheduleKind = 'geen' | 'eenmalig' | 'terugkerend' | 'contactritme'
export type UnitKind = 'week' | 'maand' | 'jaar'
export type StatusChange = 'pauzeren' | 'hervatten' | 'beeindigen'

export interface Candidate {
  kind: 'contact' | 'organisatie'
  id: string
  name: string
  organisation_name?: string | null
  role?: string | null
  last_contact_on?: string | null
  score: number
  reason?: string | null
}

export interface MentionChoice {
  type: 'contact' | 'organisatie' | 'nieuw_contact' | 'geen'
  id?: string | null
  name?: string | null
  organisation_name?: string | null
  email?: string | null
}

export interface Mention {
  ref: string
  kind: 'persoon' | 'organisatie'
  name: string
  organisation_hint?: string | null
  role_hint?: string | null
  quote?: string | null
  certainty: MentionCertainty
  basis?: string | null
  candidates: Candidate[]
  chosen?: MentionChoice | null
}

export interface FollowUp {
  schedule?: ScheduleKind | null
  interval_count?: number | null
  interval_unit?: UnitKind | null
  first_due_on?: string | null
}

export interface AgreementPreview {
  deadlines: string[]
  task_from?: string | null
  lead_days?: number | null
  warnings: string[]
  deadlines_after_contact?: string[] | null
}

/** Fields every proposal carries. */
interface ProposalBase {
  ref: string
  blocked_by: string[]
  certainty: Certainty
  flags: string[]
  link_flags: string[]
  accept_default: boolean
}

export interface ActivityProposal extends ProposalBase {
  type: ActivityKind
  with_refs: string[]
  person_id?: string | null
  organisation_id?: string | null
  new_contact_ref?: string | null
  relation_missing: boolean
  occurred_on: string
  date_text?: string | null
  title?: string | null
  summary?: string | null
  body_html: string
  contact_rhythm_effect?: string | null
}

export interface TaskProposal extends ProposalBase {
  title: string
  description?: string | null
  owner_text?: string | null
  owner_user_id?: string | null
  owner_choice_ids: string[]
  due_on?: string | null
  deadline_text?: string | null
  person_id?: string | null
  organisation_id?: string | null
  new_contact_ref?: string | null
  similar_task?: { id: string; title: string; due_on?: string | null } | null
}

export interface AgreementChoice {
  id: string
  title: string
  rhythm: string
}

export interface ChangeValues {
  follow_up?: FollowUp | null
  status?: StatusChange | null
  ends_on?: string | null
  owner_user_id?: string | null
  title?: string | null
  postpone_until?: string | null
  addendum?: string | null
}

export interface AgreementChange extends ProposalBase {
  agreement_id?: string | null
  agreement_title?: string | null
  agreement_text?: string | null
  person_id?: string | null
  organisation_id?: string | null
  candidates: AgreementChoice[]
  change: ChangeValues
  quote?: string | null
  preview?: AgreementPreview | null
  superseded_by?: string | null
}

export interface NewAgreement extends ProposalBase {
  title: string
  person_id?: string | null
  organisation_id?: string | null
  new_contact_ref?: string | null
  category_text?: string | null
  category_id?: string | null
  follow_up?: FollowUp | null
  owner_user_id?: string | null
  starts_on?: string | null
  ends_on?: string | null
  description?: string | null
  quote?: string | null
  preview?: AgreementPreview | null
  converted_from?: string | null
}

export interface VoiceResult {
  version: 1
  language?: 'nl' | 'en' | null
  mentions: Mention[]
  activities: ActivityProposal[]
  tasks: TaskProposal[]
  agreement_changes: AgreementChange[]
  new_agreements: NewAgreement[]
  unplaced: string[]
  person_ids?: string[]
}

// --- Confirm, undo ------------------------------------------------------------------------

export interface Decision {
  ref: string
  accept: boolean
  values?: Record<string, unknown>
}

export interface ConfirmBody {
  decisions?: Decision[]
  include_transcript?: boolean
  fallback_note?: { type: 'contact' | 'organisatie'; id: string }
}

export interface CreatedRefs {
  contacts: { ref: string; id: string; name: string }[]
  activities: { ref: string; id: string; type: ActivityKind; occurred_on: string; title?: string | null }[]
  tasks: { ref: string; id: string; title: string; due_on?: string | null; assignee_ids: string[] }[]
  agreement_changes: { ref: string; agreement_id: string; title: string }[]
  new_agreements: { ref: string; id: string; title: string }[]
  rhythms_met: string[]
  person_ids: string[]
}

export interface ConfirmResponse {
  refs: Partial<CreatedRefs>
  undo_until: string
  rhythms_met?: string[]
}

/** The answer to a choice under "Wie". */
export interface MentionChoiceBody {
  type: 'contact' | 'organisatie' | 'nieuw_contact' | 'geen'
  id?: string
  nieuw?: { full_name: string; organisation_name?: string; email?: string }
}
